import { normalizeBusinessActivity, normalizeCompanyName } from '../lib/normalization'
import { getSupabaseClient } from '../lib/supabase'

import { imageService, type StoredImageMetadata } from './imageService'

import type { Company, CompanyEmail, CompanyImage } from '../types/company'
import type { Json, Tables } from '../types/database'
import type { CompanyMutationInput, EditCompanyMutationInput } from '../types/forms'

type CompanyRow = Tables<'companies'>
type EmailRow = Tables<'company_emails'>
type ImageRow = Tables<'company_images'>

export interface MutationResult {
  company: Company
  warning?: string
}

export interface DeleteResult {
  warning?: string
}

function friendlyDataError(error: { code?: string; message: string }) {
  if (error.code === '23505' && error.message.includes('website_normalized')) {
    return new Error('Ya existe una empresa registrada con este sitio web.')
  }
  if (error.code === '23505' && error.message.includes('email_normalized')) {
    return new Error('Un correo está repetido dentro de la empresa.')
  }
  if (
    error.message.toLowerCase().includes('permission denied') ||
    error.message.toLowerCase().includes('not allowed')
  ) {
    return new Error(
      'La operación pública no está habilitada en Supabase. Revisa la migración y las políticas RLS.',
    )
  }
  if (error.message.includes('La empresa no existe')) {
    return new Error('La empresa ya no existe o fue eliminada por otro usuario.')
  }
  return new Error(
    'Supabase no pudo completar la operación. Revisa tu conexión e inténtalo de nuevo.',
  )
}

function toImage(row: ImageRow): CompanyImage {
  return { ...row, public_url: imageService.getPublicUrl(row.storage_path) }
}

function combineRows(
  companyRows: CompanyRow[],
  emailRows: EmailRow[],
  imageRows: ImageRow[],
): Company[] {
  const emailsByCompany = new Map<string, CompanyEmail[]>()
  for (const email of emailRows) {
    const current = emailsByCompany.get(email.company_id) ?? []
    current.push(email)
    emailsByCompany.set(email.company_id, current)
  }

  const imagesByCompany = new Map(imageRows.map((image) => [image.company_id, toImage(image)]))

  return companyRows.map((company) => ({
    ...company,
    emails: emailsByCompany.get(company.id) ?? [],
    image: imagesByCompany.get(company.id) ?? null,
  }))
}

async function fetchCompany(companyId: string): Promise<Company> {
  const supabase = getSupabaseClient()
  const [companyResult, emailsResult, imageResult] = await Promise.all([
    supabase.from('companies').select('*').eq('id', companyId).single(),
    supabase.from('company_emails').select('*').eq('company_id', companyId).order('email'),
    supabase.from('company_images').select('*').eq('company_id', companyId).maybeSingle(),
  ])

  const error = companyResult.error ?? emailsResult.error ?? imageResult.error
  if (error || !companyResult.data) {
    throw new Error(
      'La operación terminó, pero no fue posible actualizar la vista. Pulsa Reintentar.',
    )
  }

  return {
    ...companyResult.data,
    emails: emailsResult.data ?? [],
    image: imageResult.data ? toImage(imageResult.data) : null,
  }
}

function normalizedMutation(input: CompanyMutationInput) {
  return {
    p_name: normalizeCompanyName(input.name),
    p_business_activity: normalizeBusinessActivity(input.businessActivity),
    p_website_url: input.websiteUrl,
    p_website_normalized: input.websiteNormalized,
    p_emails: input.emails.map((email) => email.trim().toLowerCase()),
  }
}

async function assertWebsiteIsAvailable(websiteNormalized: string, excludeCompanyId?: string) {
  let query = getSupabaseClient()
    .from('companies')
    .select('id')
    .eq('website_normalized', websiteNormalized)
    .limit(1)

  if (excludeCompanyId) query = query.neq('id', excludeCompanyId)

  const { data, error } = await query.maybeSingle()
  if (error) {
    throw new Error('No fue posible comprobar si el sitio ya está registrado. Inténtalo de nuevo.')
  }
  if (data) throw new Error('Ya existe una empresa registrada con este sitio web.')
}

function toImageJson(image: StoredImageMetadata | null): Json {
  if (!image) return null
  return {
    storage_path: image.storage_path,
    original_name: image.original_name,
    mime_type: image.mime_type,
    size_bytes: image.size_bytes,
  }
}

async function compensateUpload(image: StoredImageMetadata | null) {
  if (!image) return undefined
  try {
    await imageService.removeCompanyImage(image.storage_path)
    return undefined
  } catch {
    return ' Además, no se pudo limpiar automáticamente el archivo recién subido.'
  }
}

async function findCompanyUsingImage(storagePath: string): Promise<string | null | undefined> {
  const { data, error } = await getSupabaseClient()
    .from('company_images')
    .select('company_id')
    .eq('storage_path', storagePath)
    .maybeSingle()

  if (error) return undefined
  return data?.company_id ?? null
}

async function reportCleanupFailure(queueId: string, error: unknown) {
  const message = error instanceof Error ? error.message : 'Error de Storage sin detalle.'
  await getSupabaseClient().rpc('report_storage_deletion_failure', {
    p_queue_id: queueId,
    p_error: message,
  })
}

async function processQueuedDeletion(queueId: string, storagePath: string): Promise<boolean> {
  try {
    await imageService.removeCompanyImage(storagePath)
    const { error } = await getSupabaseClient().rpc('complete_storage_deletion', {
      p_queue_id: queueId,
    })
    if (error) throw error
    return true
  } catch (error) {
    await reportCleanupFailure(queueId, error)
    return false
  }
}

async function retryPendingStorageDeletions() {
  const { data, error } = await getSupabaseClient().rpc('list_pending_storage_deletions', {
    p_limit: 20,
  })

  if (error || !data?.length) return
  await Promise.all(data.map((item) => processQueuedDeletion(item.id, item.storage_path)))
}

export const companyRepository = {
  async list(): Promise<Company[]> {
    const supabase = getSupabaseClient()
    await retryPendingStorageDeletions()
    const [companiesResult, emailsResult, imagesResult] = await Promise.all([
      supabase.from('companies').select('*').order('updated_at', { ascending: false }),
      supabase.from('company_emails').select('*').order('email'),
      supabase.from('company_images').select('*'),
    ])

    const error = companiesResult.error ?? emailsResult.error ?? imagesResult.error
    if (error) throw friendlyDataError(error)

    return combineRows(companiesResult.data ?? [], emailsResult.data ?? [], imagesResult.data ?? [])
  },

  async create(input: CompanyMutationInput): Promise<MutationResult> {
    const companyId = crypto.randomUUID()
    let uploadedImage: StoredImageMetadata | null = null

    await assertWebsiteIsAvailable(input.websiteNormalized)
    if (input.image) uploadedImage = await imageService.uploadCompanyImage(companyId, input.image)

    const { error } = await getSupabaseClient().rpc('create_company', {
      p_company_id: companyId,
      ...normalizedMutation(input),
      p_image: toImageJson(uploadedImage),
    })

    if (error) {
      if (uploadedImage) {
        const committedCompanyId = await findCompanyUsingImage(uploadedImage.storage_path)
        if (committedCompanyId === companyId) {
          return { company: await fetchCompany(companyId) }
        }
        if (committedCompanyId === undefined) {
          throw new Error(
            'No se pudo confirmar el resultado del guardado. El archivo se conservó para evitar perder una imagen posiblemente confirmada; usa Reintentar antes de repetir la operación.',
          )
        }
      }
      const cleanupNote = await compensateUpload(uploadedImage)
      const friendlyError = friendlyDataError(error)
      throw new Error(`${friendlyError.message}${cleanupNote ?? ''}`)
    }

    return { company: await fetchCompany(companyId) }
  },

  async update(companyId: string, input: EditCompanyMutationInput): Promise<MutationResult> {
    let uploadedImage: StoredImageMetadata | null = null
    await assertWebsiteIsAvailable(input.websiteNormalized, companyId)
    if (input.image) uploadedImage = await imageService.uploadCompanyImage(companyId, input.image)

    const imageAction = uploadedImage ? 'replace' : input.removeCurrentImage ? 'remove' : 'keep'
    const { data, error } = await getSupabaseClient().rpc('update_company', {
      p_company_id: companyId,
      ...normalizedMutation(input),
      p_image_action: imageAction,
      p_image: toImageJson(uploadedImage),
    })

    if (error) {
      if (uploadedImage) {
        const committedCompanyId = await findCompanyUsingImage(uploadedImage.storage_path)
        if (committedCompanyId === companyId) {
          await retryPendingStorageDeletions()
          return { company: await fetchCompany(companyId) }
        }
        if (committedCompanyId === undefined) {
          throw new Error(
            'No se pudo confirmar el resultado de la actualización. La imagen nueva se conservó para evitar eliminar un archivo posiblemente confirmado; usa Reintentar.',
          )
        }
      }
      const cleanupNote = await compensateUpload(uploadedImage)
      const friendlyError = friendlyDataError(error)
      throw new Error(`${friendlyError.message}${cleanupNote ?? ''}`)
    }

    const previousPath = data?.[0]?.previous_storage_path
    const deletionQueueId = data?.[0]?.deletion_queue_id
    let warning: string | undefined
    if (previousPath && deletionQueueId) {
      const cleaned = await processQueuedDeletion(deletionQueueId, previousPath)
      if (!cleaned) {
        warning =
          'Los datos se guardaron y la imagen anterior quedó en la cola de limpieza. El sistema volverá a intentar eliminarla al recargar.'
      }
    }

    return { company: await fetchCompany(companyId), warning }
  },

  async delete(companyId: string): Promise<DeleteResult> {
    const { data, error } = await getSupabaseClient().rpc('delete_company', {
      p_company_id: companyId,
    })
    if (error) throw friendlyDataError(error)

    const storagePath = data?.[0]?.storage_path
    const deletionQueueId = data?.[0]?.deletion_queue_id
    if (!storagePath) return {}

    if (!deletionQueueId) {
      return {
        warning:
          'La empresa se eliminó, pero no se recibió un identificador de limpieza para su imagen.',
      }
    }

    const cleaned = await processQueuedDeletion(deletionQueueId, storagePath)
    return cleaned
      ? {}
      : {
          warning:
            'La empresa se eliminó y su imagen quedó en la cola durable de limpieza. El sistema volverá a intentarlo al recargar.',
        }
  },
}
